import { Upload } from "lucide-react";
import React, { useState } from "react";

import { cn } from "~/lib/cn";
import { BYTES_PER_MB } from "~/lib/schemas/constants";

interface FileUploadProps {
  onChange: (files: File[]) => void;
  onError?: (error: string) => void;
  isUploading?: boolean;
  progress?: number;
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: { width: number; height: number };
  label?: string;
  fullHeight?: boolean;
}

/** Drag-and-drop file picker with progress, a size limit and an error callback. */
export const FileUpload = ({
  onChange,
  onError,
  isUploading = false,
  progress = 0,
  accept = "image/*",
  maxSize,
  recommendedDimensions,
  label = "Drag & drop a file here, or click to select one",
  fullHeight = false,
}: FileUploadProps) => {
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(
    null,
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const file = files[0];

    // Check file size if maxSize is specified
    if (maxSize && file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / BYTES_PER_MB);
      onError?.(`File size exceeds ${maxSizeMB}MB limit.`);
      return;
    }

    onChange(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleClick = () => {
    fileInputRef?.click();
  };

  return (
    <div className={cn("relative w-full", fullHeight && "h-full")}>
      <div
        className={cn(
          "hover:text-text-primary flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-4 pt-6 transition-colors duration-200 hover:border-purple-700",
          fullHeight && "h-full",
          isDragActive
            ? "border-purple-700 bg-purple-50"
            : "border-input bg-white",
        )}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Upload className="h-10 w-10 text-brand-700" />
        <p className="text-text-secondary mt-4 text-sm">{label}</p>
        {(accept || maxSize || recommendedDimensions) && (
          <div className="text-text-tertiary mt-2 flex flex-col gap-1 text-center text-xs">
            {accept && <p>Accepted formats: {accept.replace(/,/g, ", ")}</p>}
            {maxSize && (
              <p>Maximum size: {Math.round(maxSize / BYTES_PER_MB)}MB</p>
            )}
            {recommendedDimensions && (
              <p>
                Recommended dimensions: {recommendedDimensions.width}x
                {recommendedDimensions.height}px
              </p>
            )}
          </div>
        )}
      </div>
      <input
        type="file"
        accept={accept}
        ref={setFileInputRef}
        className="hidden"
        onChange={handleFileInputChange}
      />
      {isUploading && (
        <div className="bg-opacity-75 absolute inset-0 flex flex-col items-center justify-center bg-white backdrop-blur">
          <div className="h-2 w-3/4 overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-brand-700"
              style={{
                width: `${progress}%`,
                transition: "width 0.5s ease-out",
              }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-600 tabular-nums">{progress}%</p>
        </div>
      )}
    </div>
  );
};
